import { useCallback, useEffect, useState } from 'react';
import type { ItemsResponse, VendorsResponse, CategoriesResponse } from '@shared/api-responses';
import type { Item, Vendor, Category, FulfillmentChannel, VendorSendMethod, SupplyProgram } from '@shared/types';
import { PROGRAM_LABELS } from '@shared/types';
import { VENDOR_SEND_METHODS, FULFILLMENT_CHANNELS, SUPPLY_PROGRAMS } from '@shared/validation';
import { apiFetch } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import Modal from '../components/Modal';
import ProgramFilter from '../components/ProgramFilter';

/**
 * Catalog management (logistics+): create, edit, and soft-delete vendors and items.
 * The route + nav that reach this page are already gated to logistics and up
 * (App.tsx / Sidebar.tsx); the Worker re-checks the role on every write.
 * Categories are admin-only: the section is hidden below admin and the Worker
 * enforces it too.
 */

/** Pull the human message out of apiFetch's `API 400: {"error":"…"}` throw. */
function errMsg(e: unknown): string {
  const m = e instanceof Error ? e.message : String(e);
  const match = m.match(/"error":"(.*?)"/);
  return match ? match[1] : m;
}

const inputCls =
  'w-full rounded-lg bg-surface-raised border border-border-subtle px-3 py-2 text-sm text-white placeholder-zinc-500';
const labelCls = 'block text-xs font-medium text-zinc-400 mb-1';

// ── Vendor form ──────────────────────────────────────────────────────
interface VendorFormState {
  name: string;
  default_email: string;
  send_method: VendorSendMethod;
  notes: string;
  is_active: boolean;
}

function emptyVendorForm(): VendorFormState {
  return { name: '', default_email: '', send_method: 'email', notes: '', is_active: true };
}

function VendorModal({
  editing,
  onClose,
  onSaved,
}: {
  editing: Vendor | 'new';
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = editing === 'new';
  const [form, setForm] = useState<VendorFormState>(
    isNew
      ? emptyVendorForm()
      : {
          name: editing.name,
          default_email: editing.default_email ?? '',
          send_method: editing.send_method,
          notes: editing.notes ?? '',
          is_active: !!editing.is_active,
        },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: form.name,
        default_email: form.default_email || null,
        send_method: form.send_method,
        notes: form.notes || null,
        is_active: form.is_active,
      };
      if (isNew) await apiFetch('/vendors', { method: 'POST', body });
      else await apiFetch(`/vendors/${editing.id}`, { method: 'PUT', body });
      onSaved();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose}>
      <h2 className="text-lg font-bold text-white mb-4">{isNew ? 'Add vendor' : 'Edit vendor'}</h2>
      <div className="space-y-3">
        <div>
          <label className={labelCls}>Name</label>
          <input
            className={inputCls}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Coffee Supplier Co"
            autoFocus
          />
        </div>
        <div>
          <label className={labelCls}>Default email</label>
          <input
            className={inputCls}
            value={form.default_email}
            onChange={(e) => setForm({ ...form, default_email: e.target.value })}
            placeholder="orders@vendor.com"
            type="email"
          />
        </div>
        <div>
          <label className={labelCls}>Send method</label>
          <select
            className={inputCls}
            value={form.send_method}
            onChange={(e) => setForm({ ...form, send_method: e.target.value as VendorSendMethod })}
          >
            {VENDOR_SEND_METHODS.map((m) => (
              <option key={m} value={m}>
                {m === 'email' ? 'Email (auto-send)' : 'Manual'}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Notes</label>
          <textarea
            className={inputCls}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-white">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          />
          Active
        </label>
        {error && <p className="text-sm text-ems-red">{error}</p>}
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-lg px-3 py-2 text-sm text-zinc-300 hover:text-white">
          Cancel
        </button>
        <button
          onClick={save}
          disabled={saving || !form.name.trim()}
          className="rounded-lg bg-dcvfd-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

// ── Category form ────────────────────────────────────────────────────
interface CategoryFormState {
  name: string;
  /** '' = shared by both programs */
  program: SupplyProgram | '';
  sort_order: string;
  is_active: boolean;
}

function CategoryModal({
  editing,
  defaultProgram,
  onClose,
  onSaved,
}: {
  editing: Category | 'new';
  defaultProgram: SupplyProgram;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = editing === 'new';
  const [form, setForm] = useState<CategoryFormState>(
    isNew
      ? { name: '', program: defaultProgram, sort_order: '0', is_active: true }
      : {
          name: editing.name,
          program: editing.program ?? '',
          sort_order: editing.sort_order?.toString() ?? '0',
          is_active: !!editing.is_active,
        },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: form.name,
        program: form.program || null,
        sort_order: form.sort_order ? Number(form.sort_order) : 0,
        is_active: form.is_active,
      };
      if (isNew) await apiFetch('/categories', { method: 'POST', body });
      else await apiFetch(`/categories/${editing.id}`, { method: 'PUT', body });
      onSaved();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose}>
      <h2 className="text-lg font-bold text-white mb-4">{isNew ? 'Add category' : 'Edit category'}</h2>
      <div className="space-y-3">
        <div>
          <label className={labelCls}>Name</label>
          <input
            className={inputCls}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Coffee"
            autoFocus
          />
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className={labelCls}>Program</label>
            <select
              className={inputCls}
              value={form.program}
              onChange={(e) => setForm({ ...form, program: e.target.value as SupplyProgram | '' })}
            >
              {SUPPLY_PROGRAMS.map((p) => (
                <option key={p} value={p}>
                  {PROGRAM_LABELS[p]}
                </option>
              ))}
              <option value="">Both (shared)</option>
            </select>
          </div>
          <div className="w-24">
            <label className={labelCls}>Sort</label>
            <input
              className={inputCls}
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
              inputMode="numeric"
            />
          </div>
        </div>
        <p className="text-xs text-zinc-500">Sections on the request form follow this order, lowest first.</p>
        <label className="flex items-center gap-2 text-sm text-white">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          />
          Active
        </label>
        {!form.is_active && (
          <p className="text-xs text-zinc-500">
            Retired categories disappear from the request form and item editor. Move any items out first.
          </p>
        )}
        {error && <p className="text-sm text-ems-red">{error}</p>}
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-lg px-3 py-2 text-sm text-zinc-300 hover:text-white">
          Cancel
        </button>
        <button
          onClick={save}
          disabled={saving || !form.name.trim()}
          className="rounded-lg bg-dcvfd-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

// ── Item form ────────────────────────────────────────────────────────
interface ItemFormState {
  name: string;
  category_id: string;
  program: SupplyProgram;
  fulfillment_channel: FulfillmentChannel;
  vendor_id: string;
  unit: string;
  sku: string;
  sort_order: string;
  is_active: boolean;
}

function emptyItemForm(defaultProgram: SupplyProgram): ItemFormState {
  return {
    name: '',
    category_id: '',
    program: defaultProgram,
    fulfillment_channel: 'grocery',
    vendor_id: '',
    unit: '',
    sku: '',
    sort_order: '0',
    is_active: true,
  };
}

function ItemModal({
  editing,
  categories,
  vendors,
  defaultProgram,
  onClose,
  onSaved,
}: {
  editing: Item | 'new';
  categories: Category[];
  vendors: Vendor[];
  /** Prefill for a new item — whatever program the list is filtered to. */
  defaultProgram: SupplyProgram;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = editing === 'new';
  const [form, setForm] = useState<ItemFormState>(
    isNew
      ? emptyItemForm(defaultProgram)
      : {
          name: editing.name,
          category_id: editing.category_id?.toString() ?? '',
          program: editing.program,
          fulfillment_channel: editing.fulfillment_channel,
          vendor_id: editing.vendor_id?.toString() ?? '',
          unit: editing.unit ?? '',
          sku: editing.sku ?? '',
          sort_order: editing.sort_order?.toString() ?? '0',
          is_active: !!editing.is_active,
        },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isVendorChannel = form.fulfillment_channel === 'vendor';

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: form.name,
        category_id: form.category_id ? Number(form.category_id) : null,
        program: form.program,
        fulfillment_channel: form.fulfillment_channel,
        vendor_id: isVendorChannel && form.vendor_id ? Number(form.vendor_id) : null,
        unit: form.unit || null,
        sku: form.sku || null,
        sort_order: form.sort_order ? Number(form.sort_order) : 0,
        is_active: form.is_active,
      };
      if (isNew) await apiFetch('/items', { method: 'POST', body });
      else await apiFetch(`/items/${editing.id}`, { method: 'PUT', body });
      onSaved();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose}>
      <h2 className="text-lg font-bold text-white mb-4">{isNew ? 'Add item' : 'Edit item'}</h2>
      <div className="space-y-3">
        <div>
          <label className={labelCls}>Name</label>
          <input
            className={inputCls}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Coffee, ground"
            autoFocus
          />
        </div>
        <div>
          <label className={labelCls}>Program</label>
          <select
            className={inputCls}
            value={form.program}
            onChange={(e) => {
              const program = e.target.value as SupplyProgram;
              // Drop a category that doesn't belong to the new program, or the
              // select would render blank while still submitting the old id.
              const current = categories.find((c) => c.id.toString() === form.category_id);
              const keep = current && (current.program === null || current.program === program);
              setForm({ ...form, program, category_id: keep ? form.category_id : '' });
            }}
          >
            {SUPPLY_PROGRAMS.map((p) => (
              <option key={p} value={p}>
                {PROGRAM_LABELS[p]}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-zinc-500">Decides which shopping list and which distro this item lands on.</p>
        </div>
        <div>
          <label className={labelCls}>Category</label>
          <select
            className={inputCls}
            value={form.category_id}
            onChange={(e) => setForm({ ...form, category_id: e.target.value })}
          >
            <option value="">— none —</option>
            {/* Shared (null-program) categories stay available to both programs. */}
            {categories
              .filter((c) => c.program === null || c.program === form.program)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Fulfillment channel</label>
          <select
            className={inputCls}
            value={form.fulfillment_channel}
            onChange={(e) => setForm({ ...form, fulfillment_channel: e.target.value as FulfillmentChannel })}
          >
            {FULFILLMENT_CHANNELS.map((c) => (
              <option key={c} value={c}>
                {c === 'grocery' ? 'Grocery (shopping list)' : 'Vendor (emailable order)'}
              </option>
            ))}
          </select>
        </div>
        {isVendorChannel && (
          <div>
            <label className={labelCls}>Vendor</label>
            <select
              className={inputCls}
              value={form.vendor_id}
              onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
            >
              <option value="">— select vendor —</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {isVendorChannel && (
          <div>
            <label className={labelCls}>Vendor item #</label>
            <input
              className={inputCls}
              value={form.sku}
              onChange={(e) => setForm({ ...form, sku: e.target.value })}
              placeholder="e.g. HDKGSFOAMG"
            />
            <p className="mt-1 text-xs text-zinc-500">
              Printed on the emailed order — it&rsquo;s how the vendor identifies the line.
            </p>
          </div>
        )}
        <div className="flex gap-3">
          <div className="flex-1">
            <label className={labelCls}>Unit</label>
            <input
              className={inputCls}
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              placeholder="case, lb, each"
            />
          </div>
          <div className="w-24">
            <label className={labelCls}>Sort</label>
            <input
              className={inputCls}
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
              inputMode="numeric"
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-white">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          />
          Active
        </label>
        {error && <p className="text-sm text-ems-red">{error}</p>}
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-lg px-3 py-2 text-sm text-zinc-300 hover:text-white">
          Cancel
        </button>
        <button
          onClick={save}
          disabled={saving || !form.name.trim() || (isVendorChannel && !form.vendor_id)}
          className="rounded-lg bg-dcvfd-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

// ── Page ─────────────────────────────────────────────────────────────
export default function ItemsVendors() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState<Item[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingVendor, setEditingVendor] = useState<Vendor | 'new' | null>(null);
  const [editingItem, setEditingItem] = useState<Item | 'new' | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [program, setProgram] = useState<SupplyProgram | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiFetch<ItemsResponse>(program ? `/items?program=${program}` : '/items'),
      apiFetch<VendorsResponse>('/vendors'),
      // Unfiltered on purpose: the item modal needs every category so it can
      // offer the right ones for whichever program the editor picks. Admins
      // also get retired ones, so the category editor can bring one back.
      apiFetch<CategoriesResponse>(isAdmin ? '/categories?includeInactive=1' : '/categories'),
    ])
      .then(([i, v, c]) => {
        setItems(i.items);
        setVendors(v.vendors);
        setCategories(c.categories);
      })
      .finally(() => setLoading(false));
  }, [program, isAdmin]);

  useEffect(() => load(), [load]);

  const vendorName = (id: number | null) => vendors.find((v) => v.id === id)?.name ?? '—';
  const activeCategories = categories.filter((c) => c.is_active);
  // The editor shows a category if it could hold items of the filtered program.
  const shownCategories = program ? categories.filter((c) => c.program === null || c.program === program) : categories;

  async function deleteVendor(v: Vendor) {
    if (!confirm(`Delete vendor "${v.name}"? This can't be undone from the app.`)) return;
    setError(null);
    try {
      await apiFetch(`/vendors/${v.id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      setError(errMsg(e));
    }
  }

  async function deleteItem(it: Item) {
    if (!confirm(`Delete item "${it.name}"? This can't be undone from the app.`)) return;
    setError(null);
    try {
      await apiFetch(`/items/${it.id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <h1 className="text-xl font-bold text-white mb-4">Items &amp; Vendors</h1>

      {error && (
        <div className="mb-4 rounded-lg bg-ems-red/15 border border-ems-red/40 px-3 py-2 text-sm text-ems-red">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-zinc-400">Loading…</p>
      ) : (
        <>
          {/* Vendors */}
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-zinc-300">Vendors ({vendors.length})</h2>
            <button
              onClick={() => setEditingVendor('new')}
              className="rounded-lg bg-dcvfd-accent/90 hover:bg-dcvfd-accent px-3 py-1.5 text-xs font-semibold text-white"
            >
              + Add vendor
            </button>
          </div>
          <div className="glass rounded-xl p-3 mb-6 space-y-1">
            {vendors.length === 0 && <p className="text-sm text-zinc-500 px-1 py-2">No vendors yet.</p>}
            {vendors.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-3 rounded-lg px-2 py-2 hover:bg-white/5">
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">
                    {v.name}
                    {!v.is_active && <span className="ml-2 text-xs text-zinc-500">(inactive)</span>}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{v.default_email ?? 'no default email'}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setEditingVendor(v)}
                    className="rounded-lg bg-surface-raised border border-border-subtle px-2.5 py-1.5 text-xs text-white"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteVendor(v)}
                    className="rounded-lg bg-ems-red/15 px-2.5 py-1.5 text-xs text-ems-red"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Categories (admin only) */}
          {isAdmin && (
            <>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-zinc-300">Categories ({shownCategories.length})</h2>
                <button
                  onClick={() => setEditingCategory('new')}
                  className="rounded-lg bg-dcvfd-accent/90 hover:bg-dcvfd-accent px-3 py-1.5 text-xs font-semibold text-white"
                >
                  + Add category
                </button>
              </div>
              <div className="glass rounded-xl p-3 mb-6 space-y-1">
                {shownCategories.length === 0 && <p className="text-sm text-zinc-500 px-1 py-2">No categories yet.</p>}
                {shownCategories.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between gap-3 rounded-lg px-2 py-2 hover:bg-white/5"
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">
                        {c.name}
                        {!c.is_active && <span className="ml-2 text-xs text-zinc-500">(retired)</span>}
                      </div>
                      <div className="text-xs text-zinc-500 truncate">
                        {c.program ? PROGRAM_LABELS[c.program] : 'Both programs'} · sort {c.sort_order}
                      </div>
                    </div>
                    <button
                      onClick={() => setEditingCategory(c)}
                      className="shrink-0 rounded-lg bg-surface-raised border border-border-subtle px-2.5 py-1.5 text-xs text-white"
                    >
                      Edit
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Items */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h2 className="text-sm font-semibold text-zinc-300">Items ({items.length})</h2>
            <div className="flex items-center gap-2">
              <ProgramFilter value={program} onChange={setProgram} />
              <button
                onClick={() => setEditingItem('new')}
                className="rounded-lg bg-dcvfd-accent/90 hover:bg-dcvfd-accent px-3 py-1.5 text-xs font-semibold text-white"
              >
                + Add item
              </button>
            </div>
          </div>
          <div className="glass rounded-xl p-3 space-y-1">
            {items.length === 0 && (
              <p className="text-sm text-zinc-500 px-1 py-2">
                {program ? `No ${PROGRAM_LABELS[program].toLowerCase()} items yet.` : 'No items yet.'}
              </p>
            )}
            {items.map((it) => (
              <div
                key={it.id}
                className="flex items-center justify-between gap-3 rounded-lg px-2 py-2 hover:bg-white/5"
              >
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">
                    {it.name}
                    {!it.is_active && <span className="ml-2 text-xs text-zinc-500">(inactive)</span>}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">
                    {PROGRAM_LABELS[it.program]} · <span className="capitalize">{it.fulfillment_channel}</span>
                    {it.fulfillment_channel === 'vendor' ? ` · ${vendorName(it.vendor_id)}` : ''}
                    {it.unit ? ` · ${it.unit}` : ''}
                    {it.sku ? ` · #${it.sku}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setEditingItem(it)}
                    className="rounded-lg bg-surface-raised border border-border-subtle px-2.5 py-1.5 text-xs text-white"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteItem(it)}
                    className="rounded-lg bg-ems-red/15 px-2.5 py-1.5 text-xs text-ems-red"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {editingVendor && (
        <VendorModal
          editing={editingVendor}
          onClose={() => setEditingVendor(null)}
          onSaved={() => {
            setEditingVendor(null);
            load();
          }}
        />
      )}
      {editingCategory && (
        <CategoryModal
          editing={editingCategory}
          defaultProgram={program ?? 'kitchen'}
          onClose={() => setEditingCategory(null)}
          onSaved={() => {
            setEditingCategory(null);
            load();
          }}
        />
      )}
      {editingItem && (
        <ItemModal
          editing={editingItem}
          categories={activeCategories}
          vendors={vendors}
          defaultProgram={program ?? 'kitchen'}
          onClose={() => setEditingItem(null)}
          onSaved={() => {
            setEditingItem(null);
            load();
          }}
        />
      )}
    </div>
  );
}
